import { FC, Fragment, useState } from 'react';
import {
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Tabs,
} from 'antd';
import rule from '@/libs/antd/rules';
import { useForm } from '@/libs/antd/form';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import ManagerForm, { useManagerForm } from '../forms/ManagerForm';
import ShareholderForm, { useShareholderForm } from '../forms/ShareholderForm';
import { NAPCompanyGroupPayload } from '@/libs/fleet-api/services/nap';
import {
	CustomerCompanyManagementInformationForm,
	CustomerCompanyShareholderInformationForm,
} from '@/pages/frm/customer-v2/CustomerPage';
import dayjs from 'dayjs';

export type NAPCompanyGroupModalForm = {
	generalInformation: NAPCompanyGroupPayload['generalInformation'];
	managementStructure: CustomerCompanyManagementInformationForm;
	shareholderStructure: CustomerCompanyShareholderInformationForm;
};

type GeneralInfoData = NAPCompanyGroupModalForm & {
	index?: number;
};

type ModalData =
	| { type: 'new' }
	| {
			type: 'edit';
			data: GeneralInfoData;
	  };

export const useGeneralInfoModalModal = (
	onSubmit: (type: ModalData['type'], data: GeneralInfoData) => void,
) => {
	const [modalData, setModalData] = useState<ModalData>();

	return {
		data: modalData,
		isOpen: Boolean(modalData),
		open: (data: ModalData) => setModalData(data),
		close: () => setModalData(undefined),
		onSubmit,
	};
};

const GeneralInfoModal: FC<ReturnType<typeof useGeneralInfoModalModal>> = (
	props,
) => {
	const generalForm = useForm<NAPCompanyGroupPayload['generalInformation']>();
	const generalDeedForm =
		useForm<
			Pick<
				NAPCompanyGroupPayload['generalInformation'],
				| 'latestManagementShareholderDeedNumber'
				| 'latestManagementShareholderDeedYear'
			>
		>();

	const managerForm = useManagerForm(() => {
		setActiveTab('shareholder');
	});

	const shareholderForm = useShareholderForm(() => {
		props.close();
		props.onSubmit(props.data!.type, {
			generalInformation: {
				...generalForm.form.getFieldsValue(),
				...generalDeedForm.form.getFieldsValue(),
			},
			managementStructure: managerForm.form.getFieldsValue(),
			shareholderStructure: shareholderForm.form.getFieldsValue(),
			index: props.data!.type === 'edit' ? props.data?.data.index : undefined,
		});
	});
	const [activeTab, setActiveTab] = useState<
		'general' | 'manager' | 'shareholder'
	>('general');

	return (
		<FleetModal
			open={props.isOpen}
			width={940}
			closable={false}
			styles={{
				footer: {
					display: 'none',
				},
			}}
			afterOpenChange={(isOpen) => {
				if (!isOpen) {
					setActiveTab('general');
					generalForm.form.resetFields();
					managerForm.form.resetFields();
					shareholderForm.form.resetFields();
				} else if (props.data?.type === 'edit') {
					const {
						generalInformation,
						managementStructure,
						shareholderStructure,
					} = props.data.data ?? {};

					generalForm.form.setFieldsValue(generalInformation);
					managerForm.form.setFieldsValue(managementStructure);
					shareholderForm.form.setFieldsValue(shareholderStructure);
				}
			}}
		>
			<FleetTitle>Add Informasi Umum Group</FleetTitle>

			<Tabs
				style={{ marginTop: 20 }}
				activeKey={activeTab}
				onChange={(tab) => setActiveTab(tab as typeof activeTab)}
				items={[
					{
						key: 'general',
						label: 'Informasi Umum Group',
						disabled: true,
						children: (
							<Form
								{...ANTD_DEFAULT_PROPS.FORM}
								form={generalForm.form}
								onFinish={() => setActiveTab('manager')}
							>
								<Row
									gutter={40}
									style={{ marginTop: 20 }}
								>
									<Col span={12}>
										<Form.Item
											name={generalForm.fieldName('entityName')}
											label="Nama Entitas"
											rules={[rule.required()]}
										>
											<Input />
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name={generalForm.fieldName('businessField')}
											label="Bidang Usaha"
											rules={[rule.required()]}
										>
											<Input />
										</Form.Item>
									</Col>
								</Row>
								<Divider />
								<Row gutter={40}>
									<Col span={12}>
										<Form.Item
											name={generalForm.fieldName('relationshipYear')}
											label="Tahun Berdiri"
											rules={[rule.required()]}
											getValueProps={(value) => ({
												value: value && dayjs(value),
											})}
											normalize={(value) =>
												value && dayjs(value).year().toString()
											}
										>
											<DatePicker.YearPicker />
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name={generalForm.fieldName('keyPerson')}
											label="Key Person"
											rules={[rule.required()]}
										>
											<Input />
										</Form.Item>
									</Col>
								</Row>
								<Row
									gutter={8}
									justify="end"
									style={{ marginTop: 60 }}
								>
									<Col style={{ width: 180 }}>
										<Button
											block
											onClick={() => props.close()}
										>
											CANCEL
										</Button>
									</Col>
									<Col style={{ width: 180 }}>
										<Button
											type="primary"
											htmlType="submit"
											block
										>
											NEXT
										</Button>
									</Col>
								</Row>
							</Form>
						),
					},
					{
						key: 'manager',
						label: 'Data Susunan Pengurus',
						disabled: true,
						children: (
							<Fragment>
								<strong>
									Berdasarkan Akta Perubahan Pengurus dan Pemegang Saham
									Terakhir
								</strong>
								<Form
									{...ANTD_DEFAULT_PROPS.FORM}
									form={generalDeedForm.form}
									style={{ marginTop: 20 }}
									onFinish={() => setActiveTab('shareholder')}
								>
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item
												name={generalDeedForm.fieldName(
													'latestManagementShareholderDeedNumber',
												)}
												label="Nomor"
											>
												<InputNumber />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={generalDeedForm.fieldName(
													'latestManagementShareholderDeedYear',
												)}
												label="Tahun"
												rules={[rule.required()]}
												getValueProps={(value) => ({
													value: value && dayjs(value),
												})}
												normalize={(value) =>
													value && dayjs(value).year().toString()
												}
											>
												<DatePicker.YearPicker />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
								</Form>

								<ManagerForm {...managerForm} />
								<Row
									gutter={8}
									justify="end"
									style={{ marginTop: 60 }}
								>
									<Col style={{ width: 180 }}>
										<Button
											block
											onClick={() => setActiveTab('general')}
										>
											BACK
										</Button>
									</Col>
									<Col style={{ width: 180 }}>
										<Button
											type="primary"
											block
											onClick={async () => {
												await generalDeedForm.form.validateFields();
												managerForm.form.submit();
											}}
										>
											NEXT
										</Button>
									</Col>
								</Row>
							</Fragment>
						),
					},
					{
						key: 'shareholder',
						label: 'Data Kepengurusan Saham',
						disabled: true,
						children: (
							<Fragment>
								<strong>
									Berdasarkan Akta Perubahan Pengurus dan Pemegang Saham
									Terakhir
								</strong>
								<Form
									{...ANTD_DEFAULT_PROPS.FORM}
									style={{ marginTop: 20 }}
								>
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item label="Nomor">
												<InputNumber
													value={
														generalDeedForm.value
															?.latestManagementShareholderDeedNumber
													}
													disabled
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Tahun">
												<Input
													value={
														generalDeedForm.value
															?.latestManagementShareholderDeedYear
													}
													disabled
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
								</Form>
								<ShareholderForm {...shareholderForm} />
								<Row
									gutter={8}
									justify="end"
									style={{ marginTop: 60 }}
								>
									<Col style={{ width: 180 }}>
										<Button
											block
											onClick={() => setActiveTab('manager')}
										>
											BACK
										</Button>
									</Col>
									<Col style={{ width: 180 }}>
										<Button
											type="primary"
											block
											onClick={() => shareholderForm.form.submit()}
										>
											NEXT
										</Button>
									</Col>
								</Row>
							</Fragment>
						),
					},
				]}
			/>
		</FleetModal>
	);
};

export default GeneralInfoModal;
